import * as api from '@/features/backlog/api';
import { Item } from '@/features/backlog/types';
import { Command } from './types';

/**
 * The three command shapes every undoable item action reduces to:
 *
 *  - snapshotCommand — a field change (inline edit, status/assignee/date pick,
 *    kanban move, drag-reparent/reorder, timeline date drag). Undo/redo re-PUT the
 *    whole before/after snapshot, so EVERY changed field — status, position,
 *    parent, dates, … — is restored in a single write, regardless of which
 *    endpoint originally made the change.
 *  - existenceCommand — a create (or duplicate). The item(s) exist now; undo
 *    soft-deletes the root (its subtree cascades), redo restores exactly that set.
 *  - removalCommand — a delete. The item(s) are already soft-deleted; undo restores
 *    them, redo soft-deletes the root again.
 *
 * Creates and deletes are thus perfectly symmetric and never re-create rows, so
 * ids, S-keys, comments, attachments and history all survive undo/redo intact.
 */

// The full set of mutable fields PUT /items/{id} expects. `position` is included so
// a move's inverse restores ordering too (the page save sites omit it, which tells
// the API to keep the current position — here we always want the snapshot's).
const itemBody = (it: Item) => ({
  board_id: it.board_id,
  parent_id: it.parent_id,
  // epic_id + type MUST be included: PUT /items/{id} is a full replace, so omitting them would
  // reset type to its default and clear the epic link on undo/redo of an unrelated field edit.
  epic_id: it.epic_id,
  title: it.title,
  description: it.description,
  status: it.status,
  priority: it.priority,
  type: it.type,
  assignee_id: it.assignee_id,
  pdm_id: it.pdm_id,
  source: it.source,
  start_date: it.start_date,
  end_date: it.end_date,
  position: it.position,
});

export const snapshotCommand = (before: Item, after: Item, label: string): Command => ({
  label,
  boardId: after.board_id,
  undo: async () => {
    await api.updateItem(before.id, itemBody(before));
  },
  redo: async () => {
    await api.updateItem(after.id, itemBody(after));
  },
});

/** A card placement (kanban/table drag: column, order, nesting). Undo/redo replay
 *  through the NARROW reparent + move endpoints — not a full-row PUT — so only
 *  parent/status/position are written (no clobber of a concurrently-edited field)
 *  and the backend keeps its move/no-op activity semantics (no phantom "updated"). */
export interface Placement {
  parent_id: string | null;
  status: string;
  position: number;
}

export const placementCommand = (
  id: string,
  before: Placement,
  after: Placement,
  boardId: string,
  label: string,
): Command => {
  const apply = async (p: Placement) => {
    // reparent writes parent_id (+position); move writes status (+position). Both
    // set the same position; either alone can't restore all three, so we run both.
    await api.reparentItem(id, p.parent_id, p.position);
    await api.moveItem(id, p.status, p.position);
  };
  return { label, boardId, undo: () => apply(before), redo: () => apply(after) };
};

export const existenceCommand = (rootId: string, ids: string[], boardId: string, label: string): Command => {
  // The exact removed set is refreshed from each soft-delete response, so a restore
  // always targets what was actually removed (root + whatever subtree it cascaded).
  let removed = ids;
  return {
    label,
    boardId,
    undo: async () => {
      const res = await api.deleteItem(rootId);
      if (res.ids?.length) removed = res.ids;
    },
    redo: async () => {
      await api.restoreItems(removed);
    },
  };
};

export const removalCommand = (rootId: string, ids: string[], boardId: string, label: string): Command => {
  let removed = ids;
  return {
    label,
    boardId,
    undo: async () => {
      await api.restoreItems(removed);
    },
    redo: async () => {
      const res = await api.deleteItem(rootId);
      if (res.ids?.length) removed = res.ids;
    },
  };
};
